// 아바타(레인 E, docs/AVATAR-ADVENTURE-PRD.md 1절): 정의 표·세트·능력치 합산(상한)·보스 드롭 굴림·거래 정보. 순수·결정적(DOM·저장 없음).
// 1차 부위는 실루엣을 바꾸지 않는 것만(머리 고리·몸 염색·망토 판·무기 빛·발밑 오라). 하의·몸 교체형은 2차.
// 능력치는 "리듬·파밍·편의"만 준다(공격·치명·HP·방어 없음). 판매 아바타(일반)는 마을 이동 속도(편의)만 허용 — validateAvatarDefs가 강제한다.
import { AFFIXES, BUFF_CAP } from './content/items';
import { REGION_GOLD } from './content/economy';
import type { GearTotals } from './gear';

export type AvatarSlot = 'head' | 'dye' | 'cape' | 'weapon' | 'aura';
export const AVATAR_SLOTS: readonly AvatarSlot[] = ['head', 'dye', 'cape', 'weapon', 'aura'];
export const AVATAR_SLOT_KO: Readonly<Record<AvatarSlot, string>> = { head: '머리 장식', dye: '상의 염색', cape: '망토', weapon: '무기 외형', aura: '오라' };

export type AvatarGrade = 'basic' | 'fine' | 'rare';
export const AVATAR_GRADES: Readonly<Record<AvatarGrade, { readonly name: string; readonly color: string }>> = {
  basic: { name: '일반', color: '#e8e8e8' },
  fine: { name: '상급', color: '#6ac8ff' },
  rare: { name: '레어', color: '#ff5ad1' },
};

/** 아바타가 줄 수 있는 능력치. townMove만 아바타 전용(마을 장면 이동 속도), 나머지는 장비 합계(GearTotals) 같은 칸에 더한다 */
export type AvatarStat = 'townMove' | 'mpRegen' | 'cooldown' | 'moveSpd' | 'dropRate' | 'gold';
export const AVATAR_STATS: readonly AvatarStat[] = ['townMove', 'mpRegen', 'cooldown', 'moveSpd', 'dropRate', 'gold'];
export const AVATAR_STAT_KO: Readonly<Record<AvatarStat, string>> = { townMove: '마을 이동 속도', mpRegen: 'MP 재생', cooldown: '재사용 감소', moveSpd: '이동 속도', dropRate: '드롭률', gold: '골드 획득' };
/** 전투(또는 파밍)에 영향을 주는 능력치 — 판매(일반) 아바타에 들어가면 안 된다 */
export const AVATAR_COMBAT_STATS: readonly AvatarStat[] = ['mpRegen', 'cooldown', 'moveSpd', 'dropRate', 'gold'];
export type AvatarStats = Partial<Record<AvatarStat, number>>;

export interface AvatarSource { readonly kind: 'start' | 'shop' | 'mid' | 'final' | 'codex'; readonly region?: number }
export interface AvatarDef {
  readonly id: string;
  readonly name: string;
  readonly slot: AvatarSlot;
  readonly grade: AvatarGrade;
  /** 외형 주색(0xRRGGBB): 머리 고리·염색·망토·칼날·오라 색 */
  readonly color: number;
  /** 보조색(망토 테두리·오라 안쪽 고리·칼날 가장자리) */
  readonly accent: number;
  readonly stats: AvatarStats;
  readonly set?: string;
  readonly source: AvatarSource;
  /** 1차 아이콘(기존 UI 아트 재사용, public/assets 기준 경로) */
  readonly icon: string;
  readonly desc: string;
}
export interface AvatarSetDef {
  readonly id: string; readonly name: string; readonly region: number;
  readonly pieces: readonly AvatarSlot[];
  readonly fx2: AvatarStats; readonly fx3: AvatarStats;
}

// ── 상한(PRD 1.4): 장비 옵션 기대치의 15% ──
export const AVATAR_BUDGET_RATIO = 0.15;
/** 마을 이동 속도(편의, 전투 아님) 합 상한 */
export const AVATAR_TOWN_MOVE_CAP = 0.25;
/** 장비 옵션 기대치: 그 옵션이 붙는 부위 수 × 범위 가운데값(옵션은 아이템 레벨을 따라 커지지 않는다) */
export function gearOptionExpect(stat: Exclude<AvatarStat, 'townMove'>): number {
  const a = AFFIXES.find((x) => x.id === stat);
  return a ? a.slots.length * (a.range[0] + a.range[1]) / 2 : 0;
}
export const AVATAR_STAT_CAP: Readonly<Record<AvatarStat, number>> = {
  townMove: AVATAR_TOWN_MOVE_CAP,
  mpRegen: gearOptionExpect('mpRegen') * AVATAR_BUDGET_RATIO,
  cooldown: gearOptionExpect('cooldown') * AVATAR_BUDGET_RATIO,
  moveSpd: gearOptionExpect('moveSpd') * AVATAR_BUDGET_RATIO,
  dropRate: gearOptionExpect('dropRate') * AVATAR_BUDGET_RATIO,
  gold: gearOptionExpect('gold') * AVATAR_BUDGET_RATIO,
};

// ── 정의 표 ──
const I = (p: string): string => 'ui/items/' + p + '.webp';
const SLOT_ICON: Readonly<Record<AvatarSlot, (r: number) => string>> = {
  head: (r) => I(`drop_r${Math.min(4, Math.max(1, r))}_helm_r`),
  dye: (r) => I(`drop_r${Math.min(4, Math.max(1, r))}_armor_r`),
  cape: () => I('armor_travel'),
  weapon: (r) => I(`drop_r${Math.min(4, Math.max(1, r))}_weapon_kael_r`),
  aura: (r) => I(`amulet_t${r <= 2 ? 1 : r <= 4 ? 2 : 3}`),
};

/** 도감 20종 보상(PRD 2.2): 능력치 없는 오라 색. 도감 수에 함께 세어진다 */
export const CODEX_AURA_ID = 'av_codex_aura';
const CODEX: readonly AvatarDef[] = [
  { id: CODEX_AURA_ID, name: '수집가의 황금 오라', slot: 'aura', grade: 'basic', color: 0xffd86a, accent: 0xfff6d0, stats: {}, source: { kind: 'codex' }, icon: I('amulet_t3'), desc: '도감을 스무 종 채운 모험가에게 허락되는 금빛 발밑 고리. 능력치는 없다.' },
];

const START: readonly AvatarDef[] = [
  { id: 'av_travel_cape', name: '여행자 망토', slot: 'cape', grade: 'basic', color: 0x6a4e34, accent: 0xc89a52, stats: { townMove: 0.1 }, source: { kind: 'start' }, icon: I('armor_travel'), desc: '먼 길을 걷는 이의 낡은 망토. 마을을 오갈 때 발걸음이 가볍다.' },
  { id: 'av_trainee_band', name: '수련생 머리띠', slot: 'head', grade: 'basic', color: 0xe8e8f2, accent: 0xb3202f, stats: {}, source: { kind: 'start' }, icon: I('drop_r1_helm_c'), desc: '교관이 처음 매어 주는 흰 머리띠.' },
  { id: 'av_crimson_dye', name: '진홍 염료', slot: 'dye', grade: 'basic', color: 0xe0485a, accent: 0xf2c14e, stats: {}, source: { kind: 'start' }, icon: I('armor_crimson'), desc: '카엘의 진홍빛으로 옷을 물들인다.' },
];

/** 지역 중간보스 상급 1종(PRD 1.6). 능력치 1줄 */
const FINE_ROWS: readonly [region: number, slot: AvatarSlot, name: string, color: number, accent: number, stats: AvatarStats, desc: string][] = [
  [1, 'head', '족장의 깃털 장식', 0x9fd06a, 0xf2c14e, { dropRate: 0.01 }, '고블린 족장이 자랑하던 깃털. 전리품 냄새를 잘 맡는다.'],
  [2, 'dye', '용암 결 염료', 0xff7a2a, 0xffd27a, { gold: 0.01 }, '굳은 용암의 결이 옷에 남는다.'],
  [3, 'cape', '묘지기의 수의 망토', 0x9fb4d6, 0x9ad8ff, { mpRegen: 0.01 }, '차가운 묘지의 고요가 마력을 가라앉힌다.'],
  [4, 'aura', '독안개 고리', 0x9cff6a, 0x3a7a3a, { cooldown: 0.005 }, '발밑에 옅은 독안개가 맴돈다.'],
  [5, 'weapon', '경비대 의장검 외형', 0xffd27a, 0xfff2c0, { dropRate: 0.01 }, '모래 폐도 경비대의 금빛 의장검.'],
  [6, 'aura', '집행자의 사슬 고리', 0x8a5aff, 0x2a1a4a, { mpRegen: 0.01 }, '심연 집행자의 사슬이 발밑을 돈다.'],
];
const FINE: readonly AvatarDef[] = FINE_ROWS.map(([region, slot, name, color, accent, stats, desc]) => ({
  id: `av_r${region}_fine`, name, slot, grade: 'fine' as const, color, accent, stats, source: { kind: 'mid' as const, region }, icon: SLOT_ICON[slot](region), desc,
}));

/** 지역보스 레어 세트(머리·망토·오라 3부위, PRD 1.5). 부위마다 능력치 2줄 */
const SET_ROWS: readonly { id: string; name: string; region: number; color: number; accent: number; pieces: readonly [AvatarSlot, string, AvatarStats][]; fx2: AvatarStats; fx3: AvatarStats }[] = [
  { id: 'avset_moss', name: '이끼 순례자', region: 1, color: 0x7fcf6a, accent: 0xffd27a, fx2: { townMove: 0.05 }, fx3: { mpRegen: 0.01 },
    pieces: [['head', '이끼 순례자 화관', { mpRegen: 0.005, townMove: 0.05 }], ['cape', '이끼 순례자 망토', { dropRate: 0.005, townMove: 0.05 }], ['aura', '이끼 순례자 오라', { mpRegen: 0.005, gold: 0.005 }]] },
  { id: 'avset_ember', name: '잿불 방랑자', region: 2, color: 0xff8a3d, accent: 0xffe08a, fx2: { gold: 0.01 }, fx3: { cooldown: 0.005 },
    pieces: [['head', '잿불 방랑자 뿔관', { gold: 0.005, mpRegen: 0.005 }], ['cape', '잿불 방랑자 망토', { gold: 0.005, townMove: 0.05 }], ['aura', '잿불 방랑자 오라', { cooldown: 0.0025, dropRate: 0.005 }]] },
  { id: 'avset_frost', name: '서리 기사단', region: 3, color: 0x7fd8ff, accent: 0xe8f6ff, fx2: { mpRegen: 0.0075 }, fx3: { dropRate: 0.01 },
    pieces: [['head', '서리 기사단 투구 깃', { mpRegen: 0.005, dropRate: 0.005 }], ['cape', '서리 기사단 망토', { mpRegen: 0.005, townMove: 0.05 }], ['aura', '서리 기사단 오라', { cooldown: 0.0025, mpRegen: 0.005 }]] },
  { id: 'avset_swamp', name: '늪의 은자', region: 4, color: 0x6ab87a, accent: 0xc6ff8a, fx2: { townMove: 0.05 }, fx3: { mpRegen: 0.01 },
    pieces: [['head', '늪의 은자 덩굴관', { mpRegen: 0.005, gold: 0.005 }], ['cape', '늪의 은자 망토', { townMove: 0.05, dropRate: 0.005 }], ['aura', '늪의 은자 오라', { mpRegen: 0.005, moveSpd: 0.003 }]] },
  { id: 'avset_sand', name: '사막 순례', region: 5, color: 0xf2c14e, accent: 0xfff2c0, fx2: { dropRate: 0.01 }, fx3: { gold: 0.015 },
    pieces: [['head', '사막 순례 두건 장식', { dropRate: 0.005, gold: 0.005 }], ['cape', '사막 순례 망토', { gold: 0.005, townMove: 0.05 }], ['aura', '사막 순례 오라', { dropRate: 0.005, moveSpd: 0.003 }]] },
  { id: 'avset_abyss', name: '심연의 망령', region: 6, color: 0xa06aff, accent: 0xff5ad1, fx2: { moveSpd: 0.005 }, fx3: { cooldown: 0.005 },
    pieces: [['head', '심연의 망령 관', { cooldown: 0.0025, mpRegen: 0.005 }], ['cape', '심연의 망령 망토', { moveSpd: 0.003, townMove: 0.05 }], ['aura', '심연의 망령 오라', { cooldown: 0.0025, dropRate: 0.005 }]] },
];
export const AVATAR_SETS: readonly AvatarSetDef[] = SET_ROWS.map((s) => ({ id: s.id, name: s.name, region: s.region, pieces: s.pieces.map((p) => p[0]), fx2: s.fx2, fx3: s.fx3 }));
const RARE: readonly AvatarDef[] = SET_ROWS.flatMap((s) => s.pieces.map(([slot, name, stats]): AvatarDef => ({
  id: `av_r${s.region}_${slot}`, name, slot, grade: 'rare', color: s.color, accent: s.accent, stats, set: s.id, source: { kind: 'final', region: s.region }, icon: SLOT_ICON[slot](s.region),
  desc: `${s.name} 세트. 지역 ${s.region} 지역보스에게서 드물게 얻는다.`,
})));

/** 전용 아이콘(E4, Codex CLI image_gen, tools/concept/jobs/avatar-icons.codex.json → public/assets/ui/avatars). 위 표의 icon은 이 그림이 없을 때 쓰던 1차 재사용 아트 */
export const avatarIconPath = (id: string): string => `ui/avatars/avatar_${id}.webp`;
export const AVATARS: readonly AvatarDef[] = [...START, ...FINE, ...RARE, ...CODEX].map((a) => ({ ...a, icon: avatarIconPath(a.id) }));
const AVATAR_BY_ID = new Map(AVATARS.map((a) => [a.id, a]));
const SET_BY_ID = new Map(AVATAR_SETS.map((s) => [s.id, s]));
export function avatarDef(id: string | undefined | null): AvatarDef | undefined { return id ? AVATAR_BY_ID.get(id) : undefined; }
export function avatarSetDef(id: string | undefined): AvatarSetDef | undefined { return id ? SET_BY_ID.get(id) : undefined; }
/** 시작 지급(저장이 처음 만들어질 때 한 번) */
export const AVATAR_START_IDS: readonly string[] = START.map((a) => a.id);

/** 정의 표 규칙 검사(테스트가 부른다). 문제 목록(빈 배열 = 통과) */
export function validateAvatarDefs(): string[] {
  const out: string[] = [];
  const ids = new Set<string>();
  for (const a of AVATARS) {
    if (ids.has(a.id)) out.push('중복 id ' + a.id);
    ids.add(a.id);
    const keys = Object.keys(a.stats) as AvatarStat[];
    if (a.grade === 'basic' && keys.some((k) => AVATAR_COMBAT_STATS.includes(k))) out.push('일반(판매) 아바타에 전투 능력치 ' + a.id);
    if (a.grade === 'fine' && keys.length > 1) out.push('상급은 능력치 1줄 ' + a.id);
    if (a.grade === 'rare' && (keys.length > 2 || !a.set)) out.push('레어는 능력치 2줄 이하 + 세트 ' + a.id);
    for (const k of keys) if (!(a.stats[k]! > 0) || a.stats[k]! > AVATAR_STAT_CAP[k]) out.push('능력치 범위 ' + a.id + ' ' + k);
  }
  return out;
}

// ── 능력치 합산 ──
export interface AvatarTotals {
  townMove: number; mpRegen: number; cooldown: number; moveSpd: number; dropRate: number; gold: number;
  /** 상한에 걸려 잘린 능력치 수(창의 "상한 도달" 표시) */
  capped: number;
}
export function makeAvatarTotals(): AvatarTotals { return { townMove: 0, mpRegen: 0, cooldown: 0, moveSpd: 0, dropRate: 0, gold: 0, capped: 0 }; }
function addStats(out: AvatarTotals, s: AvatarStats): void {
  for (const k of AVATAR_STATS) { const v = s[k]; if (v) out[k] += v; }
}
export interface AvatarSetProgress { readonly def: AvatarSetDef; readonly count: number; readonly on2: boolean; readonly on3: boolean }
/** 장착한 아바타의 세트 진행(창 표시·합산이 같은 판정) */
export function avatarSets(equipped: readonly (string | null | undefined)[]): AvatarSetProgress[] {
  const counts = new Map<string, number>();
  for (const id of equipped) { const s = avatarDef(id)?.set; if (s) counts.set(s, (counts.get(s) ?? 0) + 1); }
  const out: AvatarSetProgress[] = [];
  for (const [id, count] of counts) { const def = SET_BY_ID.get(id); if (def) out.push({ def, count, on2: count >= 2, on3: count >= 3 }); }
  return out;
}
/** 장착 아바타(부위 + 세트) 합계를 능력치마다 AVATAR_STAT_CAP으로 자른다. 같은 id 두 번은 한 번만 센다 */
export function sumAvatar(out: AvatarTotals, equipped: readonly (string | null | undefined)[]): AvatarTotals {
  for (const k of AVATAR_STATS) out[k] = 0;
  out.capped = 0;
  const seen = new Set<string>();
  for (const id of equipped) { const d = avatarDef(id); if (!d || seen.has(d.id)) continue; seen.add(d.id); addStats(out, d.stats); }
  for (const s of avatarSets([...seen])) { if (s.on2) addStats(out, s.def.fx2); if (s.on3) addStats(out, s.def.fx3); }
  for (const k of AVATAR_STATS) {
    const cap = AVATAR_STAT_CAP[k];
    if (out[k] > cap + 1e-9) { out[k] = cap; out.capped++; }
    out[k] = Math.round(out[k] * 1e6) / 1e6;
  }
  return out;
}
/** 아바타 합계를 장비 합계에 더하고 버프 합산 캡(PRD 4.2)을 다시 건다. townMove는 장비 칸이 없어 GameSim이 따로 쓴다 */
export function applyAvatarTotals(gear: GearTotals, a: AvatarTotals): GearTotals {
  gear.mpRegen += a.mpRegen; gear.moveSpd += a.moveSpd;
  gear.cooldown = Math.min(BUFF_CAP.cooldown, gear.cooldown + a.cooldown);
  gear.dropRate = Math.min(BUFF_CAP.dropRate, gear.dropRate + a.dropRate);
  gear.gold = Math.min(BUFF_CAP.gold, gear.gold + a.gold);
  return gear;
}

// ── 보스 드롭(PRD 1.6) — 전용 난수, 레인 B 드롭 표와 별개 ──
export const AVATAR_DROP = { mid: 0.025, final: 0.015 } as const;
/** 보스 처치 한 번의 굴림. 나온 아바타 id(없으면 null). rand는 [0,1) */
export function rollAvatarDrop(rand: () => number, region: number, tier: 'mid' | 'final'): string | null {
  if (rand() >= AVATAR_DROP[tier]) return null;
  if (tier === 'mid') return FINE.find((a) => a.source.region === region)?.id ?? null;
  const pool = RARE.filter((a) => a.source.region === region);
  if (pool.length === 0) return null;
  return pool[Math.min(pool.length - 1, Math.floor(rand() * pool.length))].id;
}
/** 지역·보스 종류에서 나올 수 있는 아바타(모험 목표판 유니크 사냥 표) */
export function avatarsFrom(region: number, tier: 'mid' | 'final'): AvatarDef[] {
  return AVATARS.filter((a) => a.source.region === region && a.source.kind === tier);
}

// ── 거래(PRD 1.7) — 장비 tradeInfo와 같은 모양 ──
export const AVATAR_MARKET_MULT = 30;
export function avatarTradeInfo(id: string): { tradable: boolean; marketBase: number } {
  const d = avatarDef(id);
  if (!d || d.grade !== 'rare') return { tradable: false, marketBase: 0 };
  const g = REGION_GOLD[(d.source.region ?? 1) - 1];
  return { tradable: true, marketBase: g ? g.clear * AVATAR_MARKET_MULT : 0 };
}

/** 표시용 퍼센트 문자열(0.005 → "+0.5%") */
export function avatarStatText(k: AvatarStat, v: number): string {
  const p = Math.round(v * 1000) / 10;
  return `${AVATAR_STAT_KO[k]} +${p}%`;
}

// ── 제작(PRD 1.6 「제작」, E3): 같은 지역 조각 + 골드 → 그 지역 상급 / 레어 세트 한 부위 ──
/** 제작에 드는 지역 조각 수 */
export const CRAFT_SHARDS = { fine: 5, rare: 15 } as const;
/** 골드 = 그 지역 클리어 골드(REGION_GOLD.clear) × 계수. 조각이 병목이라 골드는 "한 판 클리어 몇 번" 수준으로 얕게 둔다 */
export const CRAFT_GOLD_MULT = { fine: 2, rare: 6 } as const;
export interface CraftRecipe { readonly id: string; readonly grade: 'fine' | 'rare'; readonly region: number; readonly shards: number; readonly gold: number }
/** 이 아바타의 제작 조건(상급·레어만, 일반·도감 보상은 null) */
export function craftRecipe(id: string): CraftRecipe | null {
  const d = avatarDef(id);
  const region = d?.source.region ?? 0;
  if (!d || (d.grade !== 'fine' && d.grade !== 'rare') || (d.source.kind !== 'mid' && d.source.kind !== 'final')) return null;
  const g = REGION_GOLD[region - 1];
  if (!g) return null;
  return { id: d.id, grade: d.grade, region, shards: CRAFT_SHARDS[d.grade], gold: g.clear * CRAFT_GOLD_MULT[d.grade] };
}
/** 지역의 제작 목록: 상급 1 + 레어 부위들(정의 순서) */
export function craftRecipes(region: number): CraftRecipe[] {
  const out: CraftRecipe[] = [];
  for (const a of AVATARS) { if (a.source.region !== region) continue; const r = craftRecipe(a.id); if (r) out.push(r); }
  return out.sort((x, y) => (x.grade === y.grade ? 0 : x.grade === 'fine' ? -1 : 1));
}
export type AvatarCraftCode = 'ok' | 'unknown' | 'owned' | 'no_shards' | 'no_gold' | 'locked' | 'readonly' | 'write_failed';
/** 제작 가능 판정(UI 표시·GameSim 사전 검사가 같은 함수). shards = 지역 번호 문자열 → 수 */
export function craftCheck(r: CraftRecipe, shards: Readonly<Record<string, number>>, gold: number, owned: readonly string[]): AvatarCraftCode {
  if (owned.includes(r.id)) return 'owned';
  if ((shards[String(r.region)] ?? 0) < r.shards) return 'no_shards';
  if (!(gold >= r.gold)) return 'no_gold';
  return 'ok';
}
export const CRAFT_CODE_KO: Readonly<Record<AvatarCraftCode, string>> = {
  ok: '제작 완료', unknown: '만들 수 없는 아바타예요', owned: '이미 가지고 있어요', no_shards: '조각이 모자라요', no_gold: '골드가 모자라요',
  locked: '마을에서만 만들 수 있어요', readonly: '지금은 저장할 수 없어 만들 수 없어요', write_failed: '저장에 실패했어요. 골드는 그대로예요',
};

/** UI·렌더가 읽는 아바타 상태(game/avatarStore.ts AvatarStore가 그대로 맞춘다) */
export interface AvatarView {
  readonly revision: number;
  readonly owned: readonly string[];
  /** AVATAR_SLOTS 순서의 장착 id(없으면 null) */
  readonly equipped: readonly (string | null)[];
  /** 외형 숨기기(능력치는 유지) */
  readonly hide: boolean;
  readonly shards: Readonly<Record<string, number>>;
  readonly totals: Readonly<AvatarTotals>;
  readonly lastGain: { readonly id: string; readonly gain: 'new' | 'shard'; readonly serial: number } | null;
  /** 마지막 제작 시도(결과 띠). 일부 뷰·시험 픽스처에는 없다 */
  readonly lastCraft?: { readonly id: string; readonly code: AvatarCraftCode; readonly serial: number } | null;
}
